import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import { Extension } from "@tiptap/core";
import Suggestion from "@tiptap/suggestion";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { buildMentionSuggestion } from "./editor/mentionSuggestion";
import {
  Bold, Italic, Strikethrough, Underline as UnderlineIcon, List, ListOrdered,
  Heading1, Heading2, Heading3, Link as LinkIcon, Undo, Redo, AtSign,
  Minus, Palette, Highlighter, Quote, Type
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITY_ROUTE_MAP = {
  character: "character",
  creature: "creature",
  location: "location",
  lore: "lore",
  deity: "deity",
};

const TEXT_COLORS = [
  { value: null, label: "Default", preview: "bg-foreground" },
  { value: "#ef4444", label: "Red", preview: "bg-red-500" },
  { value: "#f97316", label: "Orange", preview: "bg-orange-500" },
  { value: "#eab308", label: "Yellow", preview: "bg-yellow-500" },
  { value: "#22c55e", label: "Green", preview: "bg-green-500" },
  { value: "#3b82f6", label: "Blue", preview: "bg-blue-500" },
  { value: "#8b5cf6", label: "Purple", preview: "bg-violet-500" },
  { value: "#D4AF37", label: "Gold", preview: "bg-[#D4AF37]" },
  { value: "#94a3b8", label: "Silver", preview: "bg-slate-400" },
];

const EntitySearch = ({ campaignId, onSelect, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!query.trim() || !campaignId) { setResults([]); return; }
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/campaigns/${campaignId}/search`, {
          params: { q: query }, withCredentials: true
        });
        setResults(res.data);
      } catch { setResults([]); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(timeout);
  }, [query, campaignId]);

  return (
    <div className="absolute top-full left-0 mt-1 z-50 bg-card border border-border rounded-sm shadow-lg w-72 p-2" data-testid="entity-search-popup">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search characters, locations, lore..."
        className="w-full text-sm bg-input border border-border rounded-sm px-2 py-1.5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        data-testid="entity-search-input"
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
          if (e.key === "Enter" && results.length > 0) onSelect(results[0]);
        }}
      />
      {loading && <p className="text-xs text-muted-foreground py-2 px-1">Searching...</p>}
      {results.length > 0 && (
        <div className="mt-1 max-h-48 overflow-y-auto">
          {results.map(r => (
            <button
              key={`${r.type}-${r.id}`}
              className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted/50 rounded-sm flex items-center gap-2"
              onClick={() => onSelect(r)}
              data-testid={`entity-result-${r.id}`}
            >
              <span className="text-[10px] uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm">{r.type}</span>
              <span className="text-foreground">{r.name}</span>
            </button>
          ))}
        </div>
      )}
      {query.trim() && !loading && results.length === 0 && (
        <p className="text-xs text-muted-foreground py-2 px-1">No entries found</p>
      )}
    </div>
  );
};

const ColorPicker = ({ editor, onClose }) => (
  <div className="absolute top-full left-0 mt-1 z-50 bg-card border border-border rounded-sm shadow-lg p-2 w-36">
    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 px-0.5">Text Colour</p>
    <div className="grid grid-cols-5 gap-1">
      {TEXT_COLORS.map(c => (
        <button
          key={c.value || "default"}
          className={`w-6 h-6 rounded-sm border border-border/50 hover:scale-110 transition-transform ${c.preview}`}
          title={c.label}
          onClick={() => {
            if (c.value) editor.chain().focus().setColor(c.value).run();
            else editor.chain().focus().unsetColor().run();
            onClose();
          }}
        />
      ))}
    </div>
  </div>
);

const MenuBar = ({ editor, campaignId }) => {
  const [showEntitySearch, setShowEntitySearch] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  if (!editor) return null;

  const handleEntitySelect = (entity) => {
    const routeType = ENTITY_ROUTE_MAP[entity.type] || entity.type;
    const href = `/campaign/${campaignId}/${routeType}/${entity.id}`;
    editor.chain().focus().insertContent({
      type: 'text',
      text: entity.name,
      marks: [{ type: 'link', attrs: { href, class: 'entity-link' } }]
    }).insertContent({ type: 'text', text: ' ' }).run();
    setShowEntitySearch(false);
  };

  const Btn = ({ active, onClick, title, children, disabled }) => (
    <Button type="button" variant="ghost" size="icon"
      className={`h-8 w-8 ${active ? "bg-muted text-foreground" : "text-muted-foreground"}`}
      onClick={onClick} title={title} disabled={disabled}>
      {children}
    </Button>
  );

  const Sep = () => <div className="w-px h-6 bg-border mx-0.5" />;

  return (
    <div className="flex flex-wrap gap-0.5 p-1.5 border-b border-border bg-card rounded-t-sm items-center">
      <Btn active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1"><Heading1 className="w-4 h-4" /></Btn>
      <Btn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2"><Heading2 className="w-4 h-4" /></Btn>
      <Btn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3"><Heading3 className="w-4 h-4" /></Btn>
      <Sep />
      <Btn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold"><Bold className="w-4 h-4" /></Btn>
      <Btn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic"><Italic className="w-4 h-4" /></Btn>
      <Btn active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline"><UnderlineIcon className="w-4 h-4" /></Btn>
      <Btn active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough"><Strikethrough className="w-4 h-4" /></Btn>
      <Sep />
      {/* Text Color */}
      <div className="relative">
        <Btn active={showColorPicker} onClick={() => { setShowColorPicker(!showColorPicker); setShowEntitySearch(false); }} title="Text Colour">
          <Palette className="w-4 h-4" />
        </Btn>
        {showColorPicker && <ColorPicker editor={editor} onClose={() => setShowColorPicker(false)} />}
      </div>
      {/* Highlight */}
      <Btn active={editor.isActive("highlight")} onClick={() => editor.chain().focus().toggleHighlight({ color: '#D4AF3740' }).run()} title="Highlight"><Highlighter className="w-4 h-4" /></Btn>
      <Sep />
      <Btn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List"><List className="w-4 h-4" /></Btn>
      <Btn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered List"><ListOrdered className="w-4 h-4" /></Btn>
      <Btn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote / Callout Box"><Quote className="w-4 h-4" /></Btn>
      {/* Horizontal Rule */}
      <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider Line"><Minus className="w-4 h-4" /></Btn>
      <Sep />
      <Btn active={editor.isActive("link")} onClick={() => { const url = window.prompt("Enter URL"); if (url) editor.chain().focus().setLink({ href: url }).run(); }} title="Add Link"><LinkIcon className="w-4 h-4" /></Btn>
      <div className="relative">
        <Btn active={showEntitySearch} onClick={() => { setShowEntitySearch(!showEntitySearch); setShowColorPicker(false); }} title="Link Entity"><AtSign className="w-4 h-4" /></Btn>
        {showEntitySearch && campaignId && (
          <EntitySearch campaignId={campaignId} onSelect={handleEntitySelect} onClose={() => setShowEntitySearch(false)} />
        )}
      </div>
      <Sep />
      <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><Undo className="w-4 h-4" /></Btn>
      <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><Redo className="w-4 h-4" /></Btn>
    </div>
  );
};

export const RichTextEditor = ({ value, onChange, placeholder = "Write your content here...", campaignId, fillHeight = false }) => {
  const params = useParams();
  const resolvedCampaignId = campaignId || params.campaignId;
  const campaignIdRef = useRef(resolvedCampaignId);
  useEffect(() => { campaignIdRef.current = resolvedCampaignId; }, [resolvedCampaignId]);

  // Mention trigger: uses @tiptap/suggestion to show the entity picker when '@' is typed.
  // On select, we delete the '@query' text and insert a styled Link mark — so the saved
  // HTML is just a normal <a class="entity-mention" href="/campaign/.../character/...">@Name</a>.
  // No custom node, no parseHTML conflicts.
  const mentionExtension = useMemo(() => Extension.create({
    name: 'entityMentionTrigger',
    addProseMirrorPlugins() {
      const editorInstance = this.editor;
      return [
        Suggestion({
          editor: this.editor,
          ...buildMentionSuggestion(() => campaignIdRef.current),
          command: ({ editor, range, props }) => {
            const { id, label, href } = props || {};
            if (!href || !label) return;
            // Delete the '@query' text, then insert "@Label" as a link.
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent([
                {
                  type: 'text',
                  text: `@${label}`,
                  marks: [{ type: 'link', attrs: { href, 'data-mention-id': id } }],
                },
                { type: 'text', text: ' ' },
              ])
              .run();
            // Strip the link mark from the trailing space so further typing isn't linked.
            try { editor.chain().focus().unsetLink().run(); } catch (e) { /* ignore */ }
            void editorInstance;
          },
        }),
      ];
    },
  }), []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.extend({
        addAttributes() {
          // Augment Link with an optional data-mention-id attribute so saved @mentions
          // are visually distinguishable on reload.
          return {
            ...this.parent?.(),
            'data-mention-id': {
              default: null,
              parseHTML: (el) => el.getAttribute('data-mention-id'),
              renderHTML: (attrs) => {
                const id = attrs['data-mention-id'];
                if (!id) return {};
                return { 'data-mention-id': id, class: 'entity-mention' };
              },
            },
          };
        },
      }).configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary hover:underline cursor-pointer",
        },
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      mentionExtension,
      Placeholder.configure({
        placeholder,
        emptyEditorClass: "is-editor-empty",
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none min-h-[200px] p-4 focus:outline-none",
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  return (
    <div className={`rich-text-editor border border-border rounded-sm bg-input ${fillHeight ? "flex flex-col h-full min-h-0" : ""}`} data-testid="rich-text-editor">
      <MenuBar editor={editor} campaignId={resolvedCampaignId} />
      <div className={fillHeight ? "flex-1 min-h-0 overflow-y-auto" : "max-h-[40vh] overflow-y-auto"}>
        <EditorContent editor={editor} className={fillHeight ? "h-full" : ""} />
      </div>
    </div>
  );
};

export const RichTextDisplay = ({ content }) => {
  if (!content) return null;

  const handleClick = (e) => {
    const link = e.target.closest('a');
    if (link) {
      const href = link.getAttribute('href');
      if (href?.startsWith('/campaign/')) {
        e.preventDefault();
        window.location.href = href;
      }
    }
  };

  return (
    <div
      className="rich-content prose prose-invert max-w-none"
      dangerouslySetInnerHTML={{ __html: content }}
      onClick={handleClick}
      data-testid="rich-text-display"
    />
  );
};

export default RichTextEditor;
